/**
 * Socket.IO client — spec §5-F15, V3 §D.
 *
 * "Board and list views apply incoming events to the TanStack Query cache
 * **without a refetch**." So `task:updated` patches the cached task in place;
 * only events that change *membership* of a list (created, deleted, moved)
 * invalidate, because those cannot be patched without knowing every filter.
 *
 * WebSocket-only transport, matching the server (§F15) — long-polling would
 * need sticky sessions the deployment deliberately does not have.
 */

import { io, type Socket } from 'socket.io-client';
import type { QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

export interface RealtimeTask {
  _id: string;
  key: string;
  [field: string]: unknown;
}

let socket: Socket | null = null;
/** Rooms this client wants, so a reconnect can restore them. */
const joined = new Set<string>();

export function getSocket(): Socket | null {
  return socket;
}

/**
 * Joins a room and returns a leaver.
 *
 * Task changes are emitted to `project:<id>`, `task:<id>` and each watcher's
 * `user:<id>` room — never workspace-wide, which would push every project's
 * traffic at everyone. A client therefore has to subscribe to what it is
 * actually looking at, or a colleague viewing the same task sees nothing.
 * The server only honours rooms inside the caller's workspace.
 */
export function subscribeToRoom(room: string): () => void {
  joined.add(room);
  socket?.emit('subscribe', { room });
  return () => {
    joined.delete(room);
    socket?.emit('unsubscribe', { room });
  };
}

export function disconnectRealtime(): void {
  socket?.disconnect();
  socket = null;
}

/**
 * Connects and wires every event to the cache.
 *
 * Returns a disposer. Safe to call again with a new token — the old connection
 * is dropped first, which is what an access-token refresh needs.
 */
export function connectRealtime(token: string, qc: QueryClient): () => void {
  disconnectRealtime();

  socket = io({
    path: '/socket.io',
    transports: ['websocket'],
    auth: { token },
    reconnection: true,
    reconnectionDelay: 500,
    reconnectionDelayMax: 10_000,
  });

  /** Patch one task everywhere it is cached, without hitting the network. */
  const patchTask = (task: RealtimeTask): void => {
    // Detail pages key by id *or* key, depending on how they were routed.
    qc.setQueryData(['task', task._id], (old: unknown) => (old ? { ...(old as object), ...task } : old));
    qc.setQueryData(['task', task.key], (old: unknown) => (old ? { ...(old as object), ...task } : old));

    qc.setQueriesData({ queryKey: ['tasks'] }, (old: unknown) => {
      if (!old || typeof old !== 'object') return old;
      const page = old as { items?: unknown[]; meta?: unknown };
      if (!Array.isArray(page.items)) return old;

      // A list is either flat tasks or groups of tasks; both are patched by id.
      const items = page.items.map((entry) => {
        const row = entry as Record<string, unknown>;
        if (Array.isArray(row.tasks)) {
          return {
            ...row,
            tasks: (row.tasks as RealtimeTask[]).map((t) =>
              t._id === task._id ? { ...t, ...task } : t,
            ),
          };
        }
        return row._id === task._id ? { ...row, ...task } : row;
      });
      return { ...page, items };
    });
  };

  const invalidateLists = (): void => {
    void qc.invalidateQueries({ queryKey: ['tasks'] });
    void qc.invalidateQueries({ queryKey: ['my-day'] });
    void qc.invalidateQueries({ queryKey: ['inbox'] });
    // V6-03: the all-projects board is server-grouped and server-ordered, so
    // there is nothing sensible to patch in place — a moved card can change
    // column, count and position at once.
    void qc.invalidateQueries({ queryKey: ['all-projects-board'] });
  };

  socket.on('task:updated', (task: RealtimeTask) => {
    patchTask(task);
    void qc.invalidateQueries({ queryKey: ['all-projects-board'] });
  });

  // A move changes rank and column — patch, then let the board settle its order.
  socket.on('task:moved', (task: RealtimeTask) => {
    patchTask(task);
    void qc.invalidateQueries({ queryKey: ['tasks'] });
    // Column headers carry the count and the WIP-limit warning; without this
    // another viewer sees the card land but the counts stay as they were.
    void qc.invalidateQueries({ queryKey: ['board-columns'] });
    void qc.invalidateQueries({ queryKey: ['all-projects-board'] });
  });

  /**
   * Chat — V5-01 §4.
   *
   * Invalidate rather than patch: a thread is small, and a hand-written merge
   * that is subtly wrong shows the wrong message to a person who is watching
   * the screen. The typing indicator is the exception — it is transient, has
   * no query to invalidate, and goes straight to the page as a DOM event.
   */
  const invalidateChat = (): void => {
    void qc.invalidateQueries({ queryKey: ['chat-messages'] });
    void qc.invalidateQueries({ queryKey: ['chat-conversations'] });
    void qc.invalidateQueries({ queryKey: ['chat-unread'] });
  };

  /**
   * V8-02 §2 — receiving the message *is* the delivery acknowledgement.
   *
   * `pingDelivered` used to fire from one place: the effect that runs when you
   * *open* a conversation. So a recipient with the app open on another
   * conversation — or on the list, or on any other page — never acknowledged
   * anything, and the sender's tick sat on a single grey ✓ for ever. That is
   * "production never advances past sent", and it is why the derivation being
   * correct and well tested changed nothing: the marks it derives from were
   * not moving.
   *
   * Acknowledging here is the honest claim, too. The socket delivered it to
   * this client; that is precisely what two grey ticks mean, and it is true
   * whether or not anybody has looked at the conversation.
   */
  /**
   * V8-02 §2 — receiving the message *is* the delivery acknowledgement.
   *
   * `pingDelivered` used to fire from exactly one place: the effect that runs
   * when you *open* a conversation. A recipient with the app open anywhere else
   * acknowledged nothing, so the sender's tick sat on a single grey ✓ for ever
   * — "production never advances past sent". The V6-05 derivation was correct
   * and well tested throughout; the marks it derives from were not moving.
   *
   * **Which event, and why it is not `chat:message:new`.** That one goes to the
   * *conversation* room, which a client joins only when it opens the
   * conversation — precisely the case that already worked. The one that reaches
   * somebody whose chat is closed is `chat:conversation:updated`, on their own
   * user room, which is how the list badge already updates. Acking there is
   * what makes two grey ticks mean what they say: the message reached this
   * person's client, whether or not they have looked at it.
   *
   * Acking unconditionally, including for your own messages: `tickFor` ignores
   * the sender's own mark, and `sendMessage` already stamps it.
   */
  const ackDelivery = (conversationId?: string): void => {
    if (!conversationId) return;
    // Lazy so that `lib/` does not depend on `features/`.
    void import('@/features/chat/api').then(({ pingDelivered }) => pingDelivered(conversationId));
  };

  socket.on('chat:message:new', (payload: unknown) => {
    invalidateChat();
    ackDelivery((payload as { conversationId?: string } | undefined)?.conversationId);
  });
  socket.on('chat:message:edited', invalidateChat);
  socket.on('chat:message:deleted', invalidateChat);

  /**
   * V7-04 §2 — a thread reply, which is deliberately *not* a new message.
   *
   * It refreshes the open thread, the parent's "3 replies" counter on the main
   * scroll, and the Threads view — and nothing else. Treating it as
   * `chat:message:new` would move the conversation to the top of everybody's
   * list and badge people who are not in the thread, which is the entire thing
   * threads exist to prevent.
   */
  socket.on('chat:thread:new', (payload: { threadRootId?: string }) => {
    void qc.invalidateQueries({ queryKey: ['chat-thread', payload?.threadRootId] });
    void qc.invalidateQueries({ queryKey: ['chat-messages'] });
    void qc.invalidateQueries({ queryKey: ['chat-threads'] });
  });

  // §3, §5 — a reaction or a pin changes a message in place for everyone
  // looking at it; neither is new traffic, so neither touches the badge.
  socket.on('chat:reaction', () => {
    void qc.invalidateQueries({ queryKey: ['chat-messages'] });
  });
  socket.on('chat:pin', () => {
    void qc.invalidateQueries({ queryKey: ['chat-messages'] });
    void qc.invalidateQueries({ queryKey: ['chat-pins'] });
  });
  /**
   * §7 — a message arriving while you are not looking at that conversation.
   *
   * The toast is raised here rather than in the page, because the page is
   * usually not mounted when it matters. A muted conversation still moves the
   * badge — muted is not ignored — but must not interrupt.
   */
  socket.on(
    'chat:conversation:updated',
    (payload: {
      conversationId?: string;
      notify?: { muted: boolean; senderName: string; kind: string };
    }) => {
      invalidateChat();
      // §2 — the ack that reaches somebody whose chat page is closed.
      ackDelivery(payload?.conversationId);

      const notify = payload?.notify;
      if (!notify || notify.muted) return;

      const viewing =
        window.location.pathname.startsWith('/chat') &&
        new URLSearchParams(window.location.search).get('c') === payload.conversationId;
      if (viewing) return;

      // No message content in the toast, matching the catch-up email: a
      // notification is a nudge, not a preview someone else can read over a
      // shoulder.
      toast(`New message from ${notify.senderName}`, {
        action: {
          label: 'Open',
          onClick: () => {
            window.location.href = `/chat?c=${payload.conversationId}`;
          },
        },
      });
    },
  );
  socket.on('chat:read', invalidateChat);
  // V6-05 §4 — somebody's ticks moved. The thread is small and the derivation
  // is the server's, so refetch rather than recompute a tick in the client.
  socket.on('chat:delivered', invalidateChat);
  // §1, §3 — the room itself changed: renamed, someone added or removed, the
  // group deleted. The conversation list and the member sheet both go.
  socket.on('chat:conversation:membership', () => {
    invalidateChat();
    void qc.invalidateQueries({ queryKey: ['chat-members'] });
  });
  socket.on('chat:typing', (payload: unknown) => {
    window.dispatchEvent(new CustomEvent('vt:chat:typing', { detail: payload }));
  });

  socket.on('task:created', invalidateLists);
  socket.on('task:deleted', invalidateLists);

  // V6-02: a delete has to land in the other viewer's thread the way a new
  // comment does — the tombstone replaces the comment in place, no reload.
  for (const event of ['comment:created', 'comment:deleted']) {
    socket.on(event, (payload: { entityId?: string }) => {
      void qc.invalidateQueries({ queryKey: ['comments'] });
      void qc.invalidateQueries({ queryKey: ['timeline'] });
      if (payload?.entityId) void qc.invalidateQueries({ queryKey: ['task'] });
    });
  }

  // V6-04: the chip, the month grid and the corrections queue all read the
  // same key, so one invalidation is enough — and a correction decided by
  // somebody else has to reach the requester's chip without a reload.
  socket.on('attendance:changed', () => {
    void qc.invalidateQueries({ queryKey: ['attendance'] });
  });
  socket.on('attendance:correction', () => {
    void qc.invalidateQueries({ queryKey: ['attendance'] });
  });

  socket.on('note:updated', () => {
    void qc.invalidateQueries({ queryKey: ['notes'] });
    void qc.invalidateQueries({ queryKey: ['note'] });
  });

  socket.on('notification:new', (payload: { title?: string; body?: string }) => {
    void qc.invalidateQueries({ queryKey: ['notifications'] });
    void qc.invalidateQueries({ queryKey: ['unread-count'] });
    if (payload?.title) toast(payload.title, { description: payload.body });
  });

  // V4 B-1: an approver watching the queue, and a requester watching their own
  // row, both learn the moment something changes. The list is paginated and
  // filtered server-side, so membership changes are invalidated, not patched.
  for (const event of ['leave:requested', 'leave:decided']) {
    socket.on(event, () => {
      void qc.invalidateQueries({ queryKey: ['leaves'] });
      void qc.invalidateQueries({ queryKey: ['leave-calendar'] });
    });
  }

  for (const event of ['sprint:started', 'sprint:closed']) {
    socket.on(event, () => {
      void qc.invalidateQueries({ queryKey: ['sprints'] });
      invalidateLists();
    });
  }

  /**
   * V8-01 §1.3 — tell the server whether this tab is actually being looked at.
   *
   * Push suppression used to key on "has a socket", which treated an open tab
   * on another desktop, a phone in a pocket and a locked machine as somebody
   * watching. They are not, and they are most of the people who reported never
   * receiving a notification.
   *
   * The heartbeat is what makes the server's staleness rule work: a tab the
   * operating system froze stops sending, its last report ages out, and push
   * resumes. Without it a sleeping laptop would claim to be visible for ever.
   */
  const reportVisibility = (): void => {
    socket?.emit('presence:visibility', {
      visible: document.visibilityState === 'visible',
    });
  };

  // §F15: on reconnect, invalidate everything **once** — the tab may have
  // missed any number of events while it was away.
  socket.on('connect', () => {
    // Rooms are per-connection, so a reconnect has to re-join them.
    for (const room of joined) socket?.emit('subscribe', { room });
    if (socket?.recovered === false) void qc.invalidateQueries();
    reportVisibility();
  });

  document.addEventListener('visibilitychange', reportVisibility);
  // Comfortably inside the server's 90s TTL, so an ordinary tab never ages out
  // by accident — only one that has genuinely stopped running.
  const heartbeat = window.setInterval(reportVisibility, 30_000);
  reportVisibility();

  return () => {
    document.removeEventListener('visibilitychange', reportVisibility);
    window.clearInterval(heartbeat);
    disconnectRealtime();
  };
}
